import { localizedStrings } from '@core/strings'
import { Card, CardProps, Chip } from '@common/material'
import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import moment from 'moment'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import UpdateOutlined from '@mui/icons-material/UpdateOutlined'
import { User } from '@core/entities/user'
import { Checklist, ChecklistStatus } from '@core/entities/checklist/Checklist'

type ChecklistCardProps = Omit<CardProps, 'id'> & {
  templateName: string
  summary: string
  status: ChecklistStatus
  location?: string
  locationName?: string
  shortUUID?: string
  createdBy: User
  createdOn?: string
  icon?: React.ReactNode
}

export const ChecklistCard: React.FC<ChecklistCardProps> = ({
  templateName,
  summary,
  status,
  createdOn,
  createdBy,
  location,
  locationName,
  shortUUID,
  icon,
  sx = {},
  children,
  ...props
}) => {
  return (
    <Card
      sx={{
        pl: 2,
        pr: icon ? 1.5 : 2,
        py: 1.5,
        ...sx,
      }}
      {...props}
    >
      <Flex>
        <Box flex={1}>
          <Text fontSize={13} color='text.secondary'>
            {templateName}
          </Text>
          <Text fontWeight={500} fontSize='1rem'>
            {summary}
          </Text>
          <Chip
            color={Checklist.getStatusChipColor(status)}
            label={Checklist.getStatusString(status)}
            sx={{ mt: 0.5, mb: 1 }}
            size='small'
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {!!locationName && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <LocationOnOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.location}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>{locationName}</Text>
              </Flex>
            )}
            {!!shortUUID && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <TextSnippetOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.uniqueId}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>{shortUUID}</Text>
              </Flex>
            )}
            <Flex alignItems='center' sx={{ gap: 3 }}>
              <Flex alignItems='center' sx={{ width: 120 }}>
                <PersonOutlined sx={{ fontSize: 16, color: 'action.active' }} />
                <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                  {localizedStrings.author}
                </Text>
              </Flex>
              <Text sx={{ fontSize: 14, flex: 1 }}>
                {createdBy.getFullName()}
              </Text>
            </Flex>
            {!!createdOn && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <UpdateOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.submitted}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {moment(createdOn).format('DD MMM YYYY [at] HH:mm')}
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
        {icon && <Box>{icon}</Box>}
      </Flex>
      {children}
    </Card>
  )
}
